import React, { useState, useEffect } from 'react';
import { Wand2, Check, RefreshCw, Loader2, Volume2 } from 'lucide-react';
import { getCurrentUser } from '../services/auth';

/**
 * The last gate: turn approved footage into a film.
 *
 * Everything before this produces ingredients - a script, a shot list, a pile of
 * clips. This is the step that used to be an afternoon in DaVinci Resolve.
 *
 * ⚠️ IT SAYS WHAT THE EDIT WILL BE BEFORE IT IS PAID FOR. A shot with no clip
 * has its line read over the shot before it, and those shots are NAMED here, not
 * counted - "2 shots will be folded" is a number nobody can act on, and the fix
 * is to go and attach a photograph.
 *
 * ⚠️ THE VOICE IS CHOSEN, NEVER DEFAULTED SILENTLY. A narration in a voice
 * nobody picked is only discovered after the film is cut.
 *
 * ⚠️ RE-CUTTING IS BILLED AGAIN AND THE BUTTON SAYS SO. It re-reads the script,
 * which is the right thing after a script change and a waste after nothing.
 */

const AUDIO_FN = '/.netlify/functions/video-audio-generate';

export default function AssembleGate({ project, draft, busy, onAssemble, onRender, script, onDuplicate }) {
  // The first couple of sentences of THIS film, so an audition is like for like.
  const sampleLine = String(script || '')
    .split(/(?<=\.)\s+/).slice(0, 2).join(' ').trim()
    || 'They called Crisfield the seafood capital of the world.';
  const [voices, setVoices] = useState(null);
  const [voiceId, setVoiceId] = useState('');
  const [music, setMusic] = useState(true);
  const [musicPrompt, setMusicPrompt] = useState('');
  const [plan, setPlan] = useState(null);
  // Auditioning a voice. Held here rather than filed anywhere - see the note in
  // video-audio-generate about not littering the Media Library with try-outs.
  const [hearing, setHearing] = useState(false);
  const [heard, setHeard] = useState(null);
  const [voiceErr, setVoiceErr] = useState(null);
  // ⚠️ SENT WITH EVERY REQUEST, NOT SAVED AT ELEVENLABS. The sliders on their
  // website are that page's state; the API never sees them.
  const [speed, setSpeed] = useState(0.95);

  const stage = project.stage;
  const running = stage === 'assembling';
  const done = stage === 'assembled';
  const report = (draft && draft.assembly) || null;

  useEffect(() => {
    let dead = false;
    const user = getCurrentUser();
    fetch(AUDIO_FN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(user && user.accessToken ? { Authorization: `Bearer ${user.accessToken}` } : {}),
      },
      body: JSON.stringify({ action: 'status' }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (dead) return;
        const list = d.voices || [];
        setVoices(list);
        // Harry is the voice the first film was cut with. Preselected as a
        // starting point, and only when the account actually has him - a voice
        // that is not there must not be silently swapped for whatever is first.
        const harry = list.find((v) => /^Harry\b/i.test(v.name));
        if (harry) setVoiceId(harry.voice_id);
      })
      .catch(() => { if (!dead) setVoices([]); });
    return () => { dead = true; };
  }, []);

  /**
   * ⚠️ IT SPEAKS THIS FILM'S OWN OPENING, NOT A STOCK SENTENCE. A voice that
   * sounds right reading "the quick brown fox" can be wrong reading your script,
   * and the whole point of listening first is that re-narrating re-times every
   * cut in the film.
   */
  const hear = async () => {
    setHearing(true); setVoiceErr(null); setHeard(null);
    try {
      const user = getCurrentUser();
      const res = await fetch(AUDIO_FN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user && user.accessToken ? { Authorization: `Bearer ${user.accessToken}` } : {}),
        },
        body: JSON.stringify({
          action: 'narration', preview: true, brand: project.brand,
          voiceId: voiceId || undefined,
          voiceSettings: { speed },
          text: sampleLine,
        }),
      });
      const d = await res.json();
      if (!res.ok || !d.audio) throw new Error(d.error || 'could not reach the voice');
      setHeard(`data:${d.contentType || 'audio/mpeg'};base64,${d.audio}`);
    } catch (e) { setVoiceErr(e.message); } finally { setHearing(false); }
  };

  const go = async () => {
    const r = await onAssemble({
      voiceId: voiceId || undefined,
      voiceSettings: { speed },
      music,
      musicPrompt: musicPrompt.trim() || undefined,
    });
    if (r) setPlan(r);
  };

  const folding = (plan && plan.folding) || [];

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">Assemble the film</div>
      <div className="card-body">

        {done && report ? (
          <div style={{ border: '1px solid #cde8d5', background: '#f2fbf5', borderRadius: 6, padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Check size={14} color="#1a7f37" />
              <strong style={{ fontSize: 13, color: 'var(--navy)' }}>
                Cut. {report.film_seconds} seconds, {report.shots_in_edit} shot{report.shots_in_edit === 1 ? '' : 's'}.
              </strong>
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-700)', lineHeight: 1.7 }}>
              Narration and {report.music_asset_id ? 'a music bed are' : 'no music bed is'} on it
              {report.end_card ? ', and it ends on the logo card.' : ', and it ends on the last shot.'}
              {report.forced_cuts && report.forced_cuts.length ? (
                <> {report.forced_cuts.length} cut{report.forced_cuts.length === 1 ? '' : 's'} fell mid sentence
                  instead of on a breath (shot{report.forced_cuts.length === 1 ? '' : 's'} {report.forced_cuts.join(', ')}).
                  Worth watching those first.</>
              ) : ' Every cut landed on a pause in the read.'}
              {report.shortfalls && report.shortfalls.length ? (
                <> {report.shortfalls.length} shot{report.shortfalls.length === 1 ? '' : 's'} ran out of footage
                  before the line finished.</>
              ) : null}
            </div>
            {report.folded_shots && report.folded_shots.length ? (
              <div style={{ fontSize: 12, color: '#b25e09', marginTop: 8 }}>
                Shot{report.folded_shots.length === 1 ? '' : 's'} {report.folded_shots.map((f) => f.number).join(', ')} had
                no clip, so those lines are read over the shot before. Nothing was cut from the script.
              </div>
            ) : null}
          </div>
        ) : null}

        {stage === 'assembling_failed' && project.stage_error ? (
          <div style={{ background: '#fdecec', color: '#c0392b', padding: '10px 12px', borderRadius: 5, marginBottom: 14, fontSize: 12 }}>
            {project.stage_error}
          </div>
        ) : null}

        {running ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--navy)', marginBottom: 14 }}>
            <Loader2 size={14} className="spin" />
            Reading the script, timing the cuts to the voice, writing the music. About two minutes.
          </div>
        ) : null}

        {!done && !running ? (
          <p style={{ fontSize: 13, lineHeight: 1.6, margin: '0 0 14px', color: 'var(--gray-700)' }}>
            This reads the approved script aloud, cuts your clips to that voice so every change of shot
            lands on a breath, writes a music bed under it, and prepares all five formats. The voice and
            the music are billed. The edit is not.
          </p>
        ) : null}

        {folding.length && !done ? (
          <div style={{ border: '1px solid #f0c48a', background: '#fff8ee', borderRadius: 6, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: 'var(--gray-700)', lineHeight: 1.7 }}>
            <strong style={{ color: '#b25e09' }}>
              {folding.length} shot{folding.length === 1 ? '' : 's'} {folding.length === 1 ? 'has' : 'have'} no clip.
            </strong>{' '}
            Those lines are read over the shot before them, which holds longer. Nothing is cut from the
            script. If one of them should be a real photograph, attach it first and assemble again.
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {folding.map((f) => (
                <li key={f.number} style={{ color: 'var(--gray-500)' }}>
                  Shot {f.number}: {String(f.purpose || '').slice(0, 90)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!running ? (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ flex: '1 1 260px' }}>
              <label className="form-label">Narrator</label>
              <select className="form-select" value={voiceId} onChange={(e) => setVoiceId(e.target.value)}>
                {voices === null ? <option value="">Loading voices…</option> : null}
                {voices && !voices.length ? (
                  <option value="">This key cannot list voices, so the default is used</option>
                ) : null}
                {(voices || []).map((v) => (
                  <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
                ))}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Speed</span>
                <input type="range" min="0.7" max="1.2" step="0.05" value={speed}
                  onChange={(e) => setSpeed(Number(e.target.value))} style={{ flex: 1, maxWidth: 150 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', minWidth: 30 }}>{speed.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <button className="btn btn-outline" disabled={hearing || !voices || !voices.length}
                  style={{ fontSize: 11, padding: '4px 10px' }} onClick={hear}>
                  {hearing ? <Loader2 size={11} className="spin" style={{ verticalAlign: -1, marginRight: 4 }} />
                    : <Volume2 size={11} style={{ verticalAlign: -1, marginRight: 4 }} />}
                  {hearing ? 'Reading…' : 'Hear this voice'}
                </button>
                {heard ? <audio controls autoPlay src={heard} style={{ height: 30, flex: 1, minWidth: 160 }} /> : null}
              </div>
              {voiceErr ? (
                <div style={{ fontSize: 11, color: '#c0392b', marginTop: 4 }}>{voiceErr}</div>
              ) : null}
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 5, lineHeight: 1.5 }}>
                It reads the opening of this film, not a stock sentence. Changing the narrator re-times
                every cut, because a different reader breathes in different places.
              </div>
            </div>
            <div style={{ flex: '1 1 260px' }}>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={music} onChange={(e) => setMusic(e.target.checked)} />
                Write a music bed
              </label>
              <input className="form-input" value={musicPrompt} disabled={!music}
                onChange={(e) => setMusicPrompt(e.target.value)}
                placeholder="blank gives a restrained documentary underscore" />
            </div>
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!done ? (
            <button className="btn btn-primary" disabled={busy || running} onClick={go}>
              <Wand2 size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
              {running ? 'Assembling…' : busy ? 'Working…' : stage === 'assembling_failed' ? 'Try again' : 'Assemble the film'}
            </button>
          ) : (
            <>
              <button className="btn btn-primary" disabled={busy} onClick={onRender}>
                <RefreshCw size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
                Render all five formats
              </button>
              <button className="btn btn-outline" disabled={busy || running} onClick={go}>Re-cut it</button>
              {onDuplicate ? (
                <button className="btn btn-ghost" disabled={busy} onClick={onDuplicate}
                  title="Copy this film so you can try a different narrator without touching this cut">
                  Try another voice on a copy
                </button>
              ) : null}
            </>
          )}
        </div>

        {done ? (
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 10 }}>
            Re-cutting reads the script again, so it costs the voice a second time. Do it after changing
            the script or replacing a shot, not otherwise.
          </div>
        ) : null}
      </div>
    </div>
  );
}
